import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, FormControlLabel, Checkbox, Divider, Alert, CircularProgress,
  useTheme, alpha,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import venueLocationService from '../../../../services/venueLocationService';
import type { LocationType } from '../../../../types/venue';

const LOCATION_TYPES: LocationType[] = ['CELLAR', 'BAR', 'STORAGE', 'FLOOR', 'FRIDGE'];

interface FormData {
  name: string;
  type: LocationType | '';
  description: string;
  isDefault: boolean;
}

const INITIAL_FORM: FormData = {
  name: '',
  type: '',
  description: '',
  isDefault: false,
};

const VenueLocationForm: React.FC = () => {
  const navigate = useNavigate();
  const { locationId } = useParams<{ locationId: string }>();
  const theme = useTheme();
  const { venueId } = useVenue();
  const isEdit = Boolean(locationId);
  const backPath = `/admin/venues/${venueId}/locations`;

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!isEdit || !locationId) return;
    setLoading(true);
    venueLocationService.getLocation(venueId, locationId)
      .then(loc => {
        setFormData({
          name: loc.name,
          type: loc.type,
          description: loc.description ?? '',
          isDefault: loc.isDefault,
        });
      })
      .catch(() => setApiError('Failed to load location.'))
      .finally(() => setLoading(false));
  }, [isEdit, locationId, venueId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Location name is required.';
    if (!formData.type) newErrors.type = 'Type is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setApiError('');
    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type as LocationType,
        description: formData.description.trim() || undefined,
        isDefault: formData.isDefault,
        active: true,
      };
      if (isEdit && locationId) {
        await venueLocationService.updateLocation(venueId, locationId, payload);
      } else {
        await venueLocationService.createLocation(venueId, payload as Parameters<typeof venueLocationService.createLocation>[1]);
      }
      navigate(backPath);
    } catch (err) {
      console.error('Failed to save location', err);
      setApiError('Failed to save location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Locations
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Location' : 'New Location'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Location Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={Boolean(errors.name)}
              helperText={errors.name}
              fullWidth
              required
              placeholder="e.g. Main Cellar"
            />
            <FormControl required error={Boolean(errors.type)} sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={e => {
                  setFormData(prev => ({ ...prev, type: e.target.value as LocationType }));
                  setErrors(prev => ({ ...prev, type: undefined }));
                }}
              >
                {LOCATION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
              {errors.type && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.type}</Typography>}
            </FormControl>
          </Box>

          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={2}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleInputChange}
              />
            }
            label="Set as default location for this venue"
          />

          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 140 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Create Location'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default VenueLocationForm;
